import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
	Search,
	MessageCircle,
	Pencil,
	Trash2,
	Plus,
	Upload,
	Copy,
	MoreVertical,
	FileDown,
	UserX,
	UserPlus,
	Loader2,
	Merge,
	User,
	Users,
	Tag as TagIcon,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback, AvatarPreview } from "../../components/ui/avatar";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { MultiSelect } from "../../components/ui/multi-select";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import AdicionarAoKanbanModal from "../../components/AdicionarAoKanbanModal";
import KanbanEtapaChip from "../../components/KanbanEtapaChip";
import NewTicketModal from "../../components/NewTicketModal";
import EmptyStateCard from "../../components/EmptyStateCard";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import NumeroComBandeira from "../../components/NumeroComBandeira";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import usePermissions from "../../hooks/usePermissions";
import { ehAdminOuSupervisor } from "../../helpers/permissaoTicket";

const reducer = (state, action) => {
	if (action.type === "LOAD_CONTACTS") {
		const contacts = action.payload;
		const newContacts = [];

		contacts.forEach((contact) => {
			const contactIndex = state.findIndex((c) => c.id === contact.id);
			if (contactIndex !== -1) {
				state[contactIndex] = contact;
			} else {
				newContacts.push(contact);
			}
		});

		return [...state, ...newContacts];
	}

	if (action.type === "UPDATE_CONTACTS") {
		const contact = action.payload;
		const contactIndex = state.findIndex((c) => c.id === contact.id);

		if (contactIndex !== -1) {
			state[contactIndex] = contact;
			return [...state];
		} else {
			return [contact, ...state];
		}
	}

	if (action.type === "DELETE_CONTACT") {
		const contactId = action.payload;

		const contactIndex = state.findIndex((c) => c.id === contactId);
		if (contactIndex !== -1) {
			state.splice(contactIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

const Contacts = () => {
	const navigate = useNavigate();

	const { user } = useContext(AuthContext);
	const { temPermissao } = usePermissions();

	// Ação destrutiva: novo sistema de permissões (contacts:delete) OU regra
	// legada do rules.js (contacts-page:deleteContact, que só admin tem).
	// + CAMADA OBRIGATÓRIA de perfil: SOMENTE admin/supervisor pode apagar.
	//   Atendente comum (perfil "user") NUNCA vê estes botões, mesmo que
	//   haja alguma permissão de banco mal configurada.
	const podeApagarContato =
		ehAdminOuSupervisor(user) &&
		(
			temPermissao("contacts:delete") ||
			Can({
				role: user?.profile,
				perform: "contacts-page:deleteContact",
				yes: () => true,
				no: () => false,
			})
		);

	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchParam, setSearchParam] = useState("");
	const [contacts, dispatch] = useReducer(reducer, []);
	const [selectedContactId, setSelectedContactId] = useState(null);
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [contatoParaConversa, setContatoParaConversa] = useState(null);
	const [deletingContact, setDeletingContact] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [hasMore, setHasMore] = useState(false);

	// Duplicados / dedup
	const [dedupDialogOpen, setDedupDialogOpen] = useState(false);
	const [dedupLoading, setDedupLoading] = useState(false);
	const [duplicateGroups, setDuplicateGroups] = useState([]);
	const [primarySelection, setPrimarySelection] = useState({});
	const [mergingKey, setMergingKey] = useState(null);

	// LGPD
	const [anonymizingContact, setAnonymizingContact] = useState(null);
	// "todos" | "contatos" | "grupos". A agenda mistura pessoas e grupos, e
	// quem procura uma conversa raramente quer os dois ao mesmo tempo.
	const [tipo, setTipo] = useState("todos");

	// Seleção em massa. Guarda ids, não os objetos: a lista é paginada e o
	// contato pode sair da memória enquanto a seleção continua valendo.
	const [selecionados, setSelecionados] = useState(() => new Set());
	const [confirmLoteOpen, setConfirmLoteOpen] = useState(false);
	const [apagandoLote, setApagandoLote] = useState(false);

	// Limpeza total: exige digitar a frase, porque um clique errado aqui leva a
	// base inteira e não há como voltar atrás.
	const [limpezaOpen, setLimpezaOpen] = useState(false);
	const [confirmacaoLimpeza, setConfirmacaoLimpeza] = useState("");
	const [limpando, setLimpando] = useState(false);

	// Carteira: etiquetas da empresa, o filtro escolhido e o painel de
	// etiquetar em lote. É como a lista de cada atendente é montada.
	const [etiquetas, setEtiquetas] = useState([]);
	const [filtroDeTags, setFiltroDeTags] = useState([]);
	const [etiquetarOpen, setEtiquetarOpen] = useState(false);
	const [tagsDoLote, setTagsDoLote] = useState([]);
	const [modoDoLote, setModoDoLote] = useState("add");
	const [etiquetando, setEtiquetando] = useState(false);
	// Muda depois de etiquetar em lote, para a lista buscar de novo.
	const [versao, setVersao] = useState(0);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [searchParam, tipo, filtroDeTags]);

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("/contacts/", {
						params: {
							searchParam,
							pageNumber,
							// "todos" não vai: o backend trata a ausência como sem filtro.
							...(tipo !== "todos" && { tipo }),
							...(filtroDeTags.length && { tagIds: filtroDeTags.map(Number) }),
						},
					});
					dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
					setHasMore(data.hasMore);
					setLoading(false);
				} catch (err) {
					toastError(err);
				}
			};
			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, pageNumber, tipo, filtroDeTags, versao]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/tags");
				setEtiquetas(Array.isArray(data) ? data : []);
			} catch (err) {
				// Sem etiquetas o filtro some e a agenda continua igual.
			}
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		const onContact = (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
			}

			if (data.action === "purge") {
				dispatch({ type: "RESET" });
				setSelecionados(new Set());
			}
		};

		socket.on("contact", onContact);

		return () => {
			try { socket.off("contact", onContact); } catch (_) {}
		};
	}, []);

	const handleSearch = (event) => {
		setSearchParam(event.target.value.toLowerCase());
	};

	const handleOpenContactModal = () => {
		setSelectedContactId(null);
		setContactModalOpen(true);
	};

	const handleCloseContactModal = () => {
		setSelectedContactId(null);
		setContactModalOpen(false);
	};

	// Abrir conversa é escolher também por qual número ela sai. Criar o ticket
	// direto aqui mandava sempre pela conexão padrão, sem a equipe saber.
	const handleSaveTicket = (contact) => {
		if (!contact) return;
		setContatoParaConversa(
			typeof contact === "object" ? contact : { id: contact }
		);
	};

	const hadleEditContact = (contactId) => {
		setSelectedContactId(contactId);
		setContactModalOpen(true);
	};

	const handleDeleteContact = async (contactId) => {
		try {
			if (!ehAdminOuSupervisor(user)) {
				toast.error("Apenas administrador ou supervisor pode excluir contato.");
				return;
			}
			await api.delete(`/contacts/${contactId}`);
			toast.success(i18n.t("contacts.toasts.deleted"));
		} catch (err) {
			toastError(err);
		}
		setDeletingContact(null);
		setSearchParam("");
		setPageNumber(1);
	};

	const handleimportContact = async () => {
		try {
			await api.post("/contacts/import");
			navigate(0);
		} catch (err) {
			toastError(err);
		}
	};

	const fetchDuplicates = async () => {
		setDedupLoading(true);
		try {
			const { data } = await api.get("/contacts/dedup/find");
			const groups = data.duplicateGroups || [];
			setDuplicateGroups(groups);
			setPrimarySelection((prev) => {
				const next = { ...prev };
				groups.forEach((group) => {
					if (!next[group.key] || !group.contacts.some((c) => c.id === next[group.key])) {
						next[group.key] = group.contacts[0]?.id;
					}
				});
				return next;
			});
		} catch (err) {
			toastError(err);
		}
		setDedupLoading(false);
	};

	const handleOpenDedupDialog = () => {
		setDedupDialogOpen(true);
		fetchDuplicates();
	};

	const handleMergeGroup = async (group) => {
		const primaryContactId = primarySelection[group.key];
		if (!primaryContactId) return;
		const duplicateContactIds = group.contacts
			.filter((c) => c.id !== primaryContactId)
			.map((c) => c.id);
		if (duplicateContactIds.length === 0) return;
		setMergingKey(group.key);
		try {
			await api.post("/contacts/dedup/merge", {
				primaryContactId,
				duplicateContactIds,
			});
			toast.success("Contatos mesclados com sucesso.");
			await fetchDuplicates();
			dispatch({ type: "RESET" });
			setPageNumber(1);
			setSearchParam("");
		} catch (err) {
			toastError(err);
		}
		setMergingKey(null);
	};

	// Abre o cartão do cliente no quadro, na primeira coluna. É o começo do
	// trabalho: a partir daí ele anda pelas colunas, e cada coluna avisa o
	// cliente do jeito que a empresa escolheu.
	const [contatoParaKanban, setContatoParaKanban] = useState(null);

	const handleExportLGPD = async (contact) => {
		try {
			const { data } = await api.get(`/contacts/${contact.id}/lgpd/export`);
			const blob = new Blob([JSON.stringify(data, null, 2)], {
				type: "application/json",
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `contato-${contact.id}-lgpd.json`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			toast.success("Dados exportados com sucesso.");
		} catch (err) {
			toastError(err);
		}
	};

	const handleAnonymizeContact = async () => {
		if (!anonymizingContact) return;
		try {
			await api.delete(`/contacts/${anonymizingContact.id}/lgpd/anonymize`);
			toast.success("Contato anonimizado com sucesso.");
			dispatch({ type: "RESET" });
			setPageNumber(1);
			setSearchParam("");
		} catch (err) {
			toastError(err);
		}
		setAnonymizingContact(null);
	};

	const loadMore = () => {
		setPageNumber((prevState) => prevState + 1);
	};

	const handleScroll = (e) => {
		if (!hasMore || loading) return;
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			loadMore();
		}
	};

	const alternarSelecao = (id) => {
		setSelecionados((atual) => {
			const novo = new Set(atual);
			if (novo.has(id)) novo.delete(id);
			else novo.add(id);
			return novo;
		});
	};

	// "Selecionar todos" marca o que está carregado na tela, não a base
	// inteira: a lista rola sob demanda e prometer mais do que se enxerga
	// levaria a apagar contato que a pessoa nunca viu.
	const todosDaTelaMarcados =
		contacts.length > 0 && contacts.every((c) => selecionados.has(c.id));

	const alternarTodos = () => {
		setSelecionados((atual) => {
			if (todosDaTelaMarcados) {
				const novo = new Set(atual);
				contacts.forEach((c) => novo.delete(c.id));
				return novo;
			}
			const novo = new Set(atual);
			contacts.forEach((c) => novo.add(c.id));
			return novo;
		});
	};

	const apagarSelecionados = async () => {
		setApagandoLote(true);
		try {
			const { data } = await api.post("/contacts/bulk-delete", {
				contactIds: [...selecionados],
			});

			if (data.comAtendimento > 0) {
				toast.success(
					`${data.removidos} contato(s) apagado(s). ${data.comAtendimento} ` +
						"ficaram porque têm conversa registrada."
				);
			} else {
				toast.success(`${data.removidos} contato(s) apagado(s).`);
			}

			setSelecionados(new Set());
			dispatch({ type: "RESET" });
			setPageNumber(1);
		} catch (err) {
			toastError(err);
		}
		setApagandoLote(false);
		setConfirmLoteOpen(false);
	};

	// Etiquetar em lote: é assim que a carteira de um atendente sai do papel —
	// marca a lista dele inteira de uma vez, em vez de contato a contato.
	const etiquetarSelecionados = async () => {
		setEtiquetando(true);
		try {
			const { data } = await api.post("/contacts/tags", {
				contactIds: [...selecionados],
				tagIds: tagsDoLote.map(Number),
				modo: modoDoLote,
			});

			const verbo =
				modoDoLote === "remove"
					? "sem a etiqueta"
					: modoDoLote === "set"
						? "com a carteira trocada"
						: "etiquetado(s)";

			toast.success(`${data.contatos} contato(s) ${verbo}.`);

			setEtiquetarOpen(false);
			setSelecionados(new Set());
			dispatch({ type: "RESET" });
			setPageNumber(1);
			setVersao((v) => v + 1);
		} catch (err) {
			toastError(err);
		}
		setEtiquetando(false);
	};

	const limparTudo = async () => {
		setLimpando(true);

		try {
			const { data } = await api.post("/contacts/purge", {
				confirmacao: confirmacaoLimpeza,
			});

			toast.success(
				`${data.contatos} contato(s), ${data.atendimentos} atendimento(s) e ` +
					`${data.mensagens} mensagem(ns) apagados.`
			);

			setSelecionados(new Set());
			dispatch({ type: "RESET" });
			setPageNumber(1);
			setLimpezaOpen(false);
		} catch (err) {
			toastError(err);
		}

		setLimpando(false);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={`Apagar ${selecionados.size} contato(s)?`}
				open={confirmLoteOpen}
				onClose={() => setConfirmLoteOpen(false)}
				onConfirm={apagarSelecionados}
			>
				Some da agenda para sempre. Quem tiver conversa registrada
				<span className="font-medium"> não é apagado</span>: apagá-lo levaria
				junto o histórico do que foi dito ao cliente.
			</ConfirmationModal>
			{/* Limpeza total. Não usa o ConfirmationModal comum de propósito: um
			    "tem certeza?" com botão vermelho é o mesmo gesto de qualquer
			    outra exclusão, e esta apaga a base inteira. Digitar a frase
			    obriga a ler o que vai acontecer. */}
			<Dialog open={limpezaOpen} onOpenChange={setLimpezaOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="text-destructive">
							Apagar todos os contatos?
						</DialogTitle>
					</DialogHeader>

					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							Isto apaga <span className="font-medium">toda a agenda</span> desta
							empresa e, junto,{" "}
							<span className="font-medium">
								todos os atendimentos e todas as conversas
							</span>{" "}
							— inclusive o histórico do que foi dito aos clientes.
						</p>

						<p className="text-sm text-muted-foreground">
							Não há como desfazer. Se ainda houver dúvida, faça um backup
							antes em <span className="font-medium">Configurações › Backup</span>.
						</p>

						<div className="space-y-1.5">
							<Label htmlFor="confirmacao-limpeza">
								Digite <span className="font-mono font-semibold">APAGAR TUDO</span>{" "}
								para confirmar
							</Label>
							<Input
								id="confirmacao-limpeza"
								value={confirmacaoLimpeza}
								onChange={(e) => setConfirmacaoLimpeza(e.target.value)}
								placeholder="APAGAR TUDO"
								autoComplete="off"
							/>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setLimpezaOpen(false)}>
							Cancelar
						</Button>
						<Button
							variant="destructive"
							onClick={limparTudo}
							disabled={
								limpando ||
								confirmacaoLimpeza.trim().toUpperCase() !== "APAGAR TUDO"
							}
						>
							{limpando && <Loader2 className="h-4 w-4 animate-spin" />}
							Apagar tudo
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<ContactModal
				open={contactModalOpen}
				onClose={handleCloseContactModal}
				contactId={selectedContactId}
			></ContactModal>
			<ConfirmationModal
				title={
					deletingContact
						? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
								// Sem nome, o título virava "Deletar ?" — ninguém confirma
								// uma exclusão sem saber o que está apagando.
								deletingContact.name || deletingContact.number || "este contato"
						  }?`
						: `${i18n.t("contacts.confirmationModal.importTitlte")}`
				}
				open={confirmOpen}
				onClose={setConfirmOpen}
				onConfirm={(e) =>
					deletingContact
						? handleDeleteContact(deletingContact.id)
						: handleimportContact()
				}
			>
				{deletingContact
					? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
					: "Todos os contatos salvos no celular conectado entram na sua lista, inclusive quem nunca falou com a empresa. Não dá para desfazer de uma vez: depois só apagando um por um."}
			</ConfirmationModal>
			<ConfirmationModal
				title={
					anonymizingContact
						? `Apagar os dados pessoais de ${anonymizingContact.name}?`
						: ""
				}
				open={!!anonymizingContact}
				onClose={() => setAnonymizingContact(null)}
				onConfirm={handleAnonymizeContact}
			>
				Nome, telefone e e-mail somem para sempre e a pessoa deixa de ser
				identificável. As conversas continuam guardadas, mas sem saber de quem
				são. Não dá para voltar atrás.
			</ConfirmationModal>

			<Dialog open={dedupDialogOpen} onOpenChange={setDedupDialogOpen}>
				<DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Contatos duplicados</DialogTitle>
						<DialogDescription>
							A mesma pessoa cadastrada mais de uma vez. Ao mesclar, as
							conversas de todas as cópias passam para o contato principal e as
							outras somem da lista.
						</DialogDescription>
					</DialogHeader>
					{dedupLoading && (
						<div className="flex justify-center py-6 text-muted-foreground">
							<Loader2 className="h-5 w-5 animate-spin" />
						</div>
					)}
					{!dedupLoading && duplicateGroups.length === 0 && (
						<p className="py-4 text-center text-sm text-muted-foreground">
							Nenhum contato duplicado encontrado.
						</p>
					)}
					{!dedupLoading && duplicateGroups.length > 0 && (
						<div className="space-y-4">
							{duplicateGroups.map((group) => (
								<div key={group.key} className="rounded-lg border p-3">
									<div className="mb-2 flex items-center justify-between gap-2">
										<Badge variant="secondary">
											{group.contacts.length} contatos
										</Badge>
										<Button
											size="sm"
											onClick={() => handleMergeGroup(group)}
											disabled={mergingKey === group.key}
										>
											{mergingKey === group.key ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<Merge className="h-4 w-4" />
											)}
											Mesclar
										</Button>
									</div>
									<p className="mb-2 text-xs text-muted-foreground">
										Selecione o contato principal. Os demais serão mesclados
										nele.
									</p>
									<div className="space-y-1">
										{group.contacts.map((c) => (
											<label
												key={c.id}
												className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
											>
												<input
													type="radio"
													name={`primary-${group.key}`}
													className="h-4 w-4 accent-primary"
													checked={primarySelection[group.key] === c.id}
													onChange={() =>
														setPrimarySelection((prev) => ({
															...prev,
															[group.key]: c.id,
														}))
													}
												/>
												<span className="flex-1 truncate font-medium">
													{c.name}
												</span>
												<span className="shrink-0 text-muted-foreground">
													{c.number}
												</span>
											</label>
										))}
									</div>
								</div>
							))}
						</div>
					)}
				</DialogContent>
			</Dialog>
			{/* Etiquetar em lote. Somar é o padrão: trocar a carteira de cem
			    contatos de uma vez apagaria as etiquetas que eles já tinham, e
			    isso precisa ser uma escolha, não o comportamento silencioso. */}
			<Dialog open={etiquetarOpen} onOpenChange={setEtiquetarOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Etiquetar contatos</DialogTitle>
						<DialogDescription>
							{selecionados.size} contato(s) selecionado(s). A etiqueta fica na
							pessoa: é ela que a campanha usa para escolher quem recebe.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label>Etiquetas</Label>
							<MultiSelect
								options={etiquetas.map((tag) => ({
									value: String(tag.id),
									label: tag.name,
									color: tag.color,
								}))}
								value={tagsDoLote}
								onChange={setTagsDoLote}
								placeholder="Selecione uma ou mais etiquetas"
							/>
						</div>
						<div className="space-y-1.5">
							<Label>O que fazer com elas</Label>
							<div className="grid gap-1.5">
								{[
									{
										valor: "add",
										titulo: "Adicionar",
										ajuda: "Cola a etiqueta sem mexer nas que já estão lá.",
									},
									{
										valor: "remove",
										titulo: "Tirar",
										ajuda: "Remove só as escolhidas.",
									},
									{
										valor: "set",
										titulo: "Substituir tudo",
										ajuda:
											"Deixa apenas as escolhidas. As outras etiquetas destes contatos são apagadas.",
									},
								].map(({ valor, titulo, ajuda }) => (
									<button
										key={valor}
										type="button"
										onClick={() => setModoDoLote(valor)}
										className={`rounded-md border p-2.5 text-left text-sm ${
											modoDoLote === valor
												? "border-primary bg-muted"
												: "hover:bg-muted/50"
										}`}
									>
										<span className="font-medium">{titulo}</span>
										<span className="block text-xs leading-relaxed text-muted-foreground">
											{ajuda}
										</span>
									</button>
								))}
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setEtiquetarOpen(false)}
							disabled={etiquetando}
						>
							Cancelar
						</Button>
						<Button
							onClick={etiquetarSelecionados}
							disabled={
								etiquetando || (modoDoLote !== "set" && tagsDoLote.length === 0)
							}
						>
							{etiquetando && <Loader2 className="h-4 w-4 animate-spin" />}
							Aplicar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<MainHeader>
				<div className="min-w-0 flex-1">
					<Title>{i18n.t("contacts.title")}</Title>
					<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
						A agenda da empresa. Quem manda mensagem entra aqui sozinho.
					</p>
				</div>
				<MainHeaderButtonsWrapper>
					<div className="relative w-full sm:w-auto">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder={i18n.t("contacts.searchPlaceholder")}
							type="search"
							value={searchParam}
							onChange={handleSearch}
							className="w-full pl-9 sm:w-56"
						/>
					</div>
					{/* Grupo e pessoa se comportam de forma diferente no atendimento;
					    misturados na mesma lista, achar um dos dois é caçada. */}
					<div className="flex items-center rounded-md border p-0.5">
						{[
							{ valor: "todos", rotulo: "Todos", Icone: null },
							{ valor: "contatos", rotulo: "Contatos", Icone: User },
							{ valor: "grupos", rotulo: "Grupos", Icone: Users },
						].map(({ valor, rotulo, Icone }) => (
							<Button
								key={valor}
								type="button"
								variant={tipo === valor ? "secondary" : "ghost"}
								size="sm"
								className="h-8 gap-1.5 px-2.5 text-xs"
								aria-pressed={tipo === valor}
								onClick={() => setTipo(valor)}
							>
								{Icone && <Icone className="h-3.5 w-3.5" />}
								{rotulo}
							</Button>
						))}
					</div>

					{/* Filtro por carteira: "me mostre só os contatos da Fernanda".
					    Sem ele, marcar a lista de um atendente exigiria caçar nome
					    por nome na agenda inteira. */}
					{etiquetas.length > 0 && (
						<div className="w-full sm:w-56">
							<MultiSelect
								options={etiquetas.map((tag) => ({
									value: String(tag.id),
									label: tag.name,
									color: tag.color,
								}))}
								value={filtroDeTags}
								onChange={setFiltroDeTags}
								placeholder="Todas as etiquetas"
							/>
						</div>
					)}

					{/* Duplicados, importar e limpar tudo são manutenção da base:
					    feitas de vez em quando, e quase sempre no computador. No
					    celular saem da barra — cabem no menu de três pontos ao
					    lado, e devolvem a tela para a lista de contatos. */}
					<TooltipProvider>
						<div className="hidden items-center gap-2 sm:flex">
						<Tooltip>
							<TooltipTrigger asChild>
								<Button variant="outline" onClick={handleOpenDedupDialog}>
									<Copy className="h-4 w-4" />
									<span className="hidden sm:inline">Duplicados</span>
								</Button>
							</TooltipTrigger>
							<TooltipContent className="max-w-xs">
								Procura a mesma pessoa cadastrada duas vezes e junta tudo num
								contato só, sem perder o histórico.
							</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button variant="outline" onClick={() => setConfirmOpen(true)}>
									<Upload className="h-4 w-4" />
									<span className="hidden sm:inline">{i18n.t("contacts.buttons.import")}</span>
								</Button>
							</TooltipTrigger>
							<TooltipContent className="max-w-xs">
								Traz para cá toda a agenda do celular conectado. Pode entrar
								gente que nunca falou com a empresa.
							</TooltipContent>
						</Tooltip>
						</div>
					</TooltipProvider>
					{/* Só administrador / quem tem permissão vê. É a ação mais destrutiva do sistema:
					    leva a agenda, os atendimentos e as conversas junto. */}
					{podeApagarContato && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="outline"
										className="hidden text-destructive hover:text-destructive sm:inline-flex"
										onClick={() => {
											setConfirmacaoLimpeza("");
											setLimpezaOpen(true);
										}}
									>
										<Trash2 className="h-4 w-4" />
										<span className="hidden sm:inline">Limpar tudo</span>
									</Button>
								</TooltipTrigger>
								<TooltipContent className="max-w-xs">
									Apaga a agenda inteira, junto com os atendimentos e as
									conversas. Não tem como desfazer.
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}
					{/* O que saiu da barra no celular entra aqui — nada some. */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								size="icon"
								className="shrink-0 sm:hidden"
								aria-label="Mais ações da agenda"
							>
								<MoreVertical className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-64">
							<DropdownMenuItem onClick={handleOpenDedupDialog}>
								<Copy className="mr-2 h-4 w-4" />
								Procurar duplicados
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setConfirmOpen(true)}>
								<Upload className="mr-2 h-4 w-4" />
								{i18n.t("contacts.buttons.import")}
							</DropdownMenuItem>
							{podeApagarContato && (
								<DropdownMenuItem
									className="text-destructive focus:text-destructive"
									onClick={() => {
										setConfirmacaoLimpeza("");
										setLimpezaOpen(true);
									}}
								>
									<Trash2 className="mr-2 h-4 w-4" />
									Limpar tudo
								</DropdownMenuItem>
							)}
						</DropdownMenuContent>
					</DropdownMenu>

					<Button className="flex-1 sm:flex-none" onClick={handleOpenContactModal}>
						<Plus className="h-4 w-4" />
						{i18n.t("contacts.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6" onScroll={handleScroll}>
			{selecionados.size > 0 && (
				<div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border bg-muted px-4 py-2.5">
					<span className="text-sm font-medium">
						{selecionados.size} selecionado(s)
					</span>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setSelecionados(new Set())}
					>
						Limpar seleção
					</Button>
					{etiquetas.length > 0 && (
						<Button
							variant="outline"
							size="sm"
							className="ml-auto"
							onClick={() => {
								setTagsDoLote([]);
								setModoDoLote("add");
								setEtiquetarOpen(true);
							}}
						>
							<TagIcon className="h-4 w-4" />
							Etiquetar selecionados
						</Button>
					)}
					{podeApagarContato && (
						<Button
							variant="destructive"
							size="sm"
							className={etiquetas.length > 0 ? "" : "ml-auto"}
							onClick={() => setConfirmLoteOpen(true)}
						>
							<Trash2 className="h-4 w-4" />
							Apagar selecionados
						</Button>
					)}
				</div>
			)}
			{/* Tabela: só do tablet para cima. No celular ela vira a lista
			    logo abaixo — coluna espremida em 390px obriga a rolar para o
			    lado, e ninguém rola tabela de lado no telefone. */}
			<div className="hidden rounded-xl border bg-card sm:block">
				<Table>
					<TableHeader className="sticky top-0 z-10 bg-card">
						<TableRow>
							<TableHead className="w-10">
								<Checkbox
									checked={todosDaTelaMarcados}
									onCheckedChange={alternarTodos}
									aria-label="Selecionar todos os contatos desta tela"
								/>
							</TableHead>
							<TableHead className="w-12" />
							<TableHead>{i18n.t("contacts.table.name")}</TableHead>
							<TableHead className="text-center">
								{i18n.t("contacts.table.whatsapp")}
							</TableHead>
							{/* O e-mail é o que menos se usa aqui e é o mais largo:
							    fora dele a tabela cabe na tela do celular sem
							    rolagem lateral. Continua visível no computador. */}
							<TableHead className="hidden text-center sm:table-cell">
								{i18n.t("contacts.table.email")}
							</TableHead>
							{/* Etapa atual do Kanban (coluna do cartão mais recente do cliente).
							    Alinha o que a equipe vê na agenda com o que o funil mostra no
							    quadro. Grupos não são trabalho individual, então não aparece na
							    aba de grupos. */}
							{tipo !== "grupos" && (
								<TableHead className="hidden text-center md:table-cell">
									Kanban
								</TableHead>
							)}
							{/* Grupo: a conexão e a fila fixas ficam na linha. É a
							    configuração que decide por qual número o grupo é
							    atendido e quem recebe o aviso — ter de abrir cada
							    grupo para conferir isso não serve. */}
							{tipo === "grupos" && (
								<>
									<TableHead className="hidden text-center md:table-cell">
										Conexão
									</TableHead>
									<TableHead className="hidden text-center md:table-cell">
										Fila
									</TableHead>
								</>
							)}

							{/* A carteira precisa ser visível na linha: é ela que decide
							    quem recebe a campanha e quem atende a resposta. */}
							{etiquetas.length > 0 && (
								<TableHead className="hidden lg:table-cell">Etiquetas</TableHead>
							)}
							<TableHead className="text-center">
								{i18n.t("contacts.table.actions")}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{contacts.map((contact) => {
							const initials = (contact.name || "?")
								.split(" ")
								.slice(0, 2)
								.map((n) => n[0])
								.join("")
								.toUpperCase();
							return (
								<TableRow
									key={contact.id}
									data-state={selecionados.has(contact.id) ? "selected" : undefined}
								>
									<TableCell>
										<Checkbox
											checked={selecionados.has(contact.id)}
											onCheckedChange={() => alternarSelecao(contact.id)}
											aria-label={`Selecionar ${contact.name || "contato sem nome"}`}
										/>
									</TableCell>
									<TableCell className="pr-0">
										<AvatarPreview
											className="h-9 w-9"
											previewUrl={contact.profilePicUrl}
											alt={contact.name}
										>
											<AvatarImage src={contact.profilePicUrl} alt={contact.name} />
											{/* Grupo com iniciais de nome parece pessoa. O ícone
											    diferencia à distância, sem precisar ler. */}
											<AvatarFallback className="text-xs">
												{contact.isGroup ? (
													<Users className="h-4 w-4" />
												) : (
													initials
												)}
											</AvatarFallback>
										</AvatarPreview>
									</TableCell>
									<TableCell className="font-medium">
										<div className="flex items-center gap-2">
											{/* Contato sem nome deixava a linha em branco: nada para ler,
											    nada para identificar antes de excluir. */}
											{contact.name || (
												<span className="italic text-muted-foreground">Sem nome</span>
											)}
											{contact.isGroup && (
												<span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
													Grupo
												</span>
											)}
											{/* Apareceu falando num grupo e ninguém cadastrou: virou
											    linha aqui para a mensagem ter dono, mas não é gente
											    que a empresa conhece. */}
											{!contact.isGroup && contact.savedInAgenda === false && (
												<span
													className="rounded-full border border-dashed px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
													title="Apareceu num grupo e ainda não foi adicionado aos contatos"
												>
													De grupo
												</span>
											)}
										</div>
									</TableCell>
									<TableCell className="text-center text-muted-foreground">
										{/* Grupo não tem telefone: o id é um número longo do próprio
										    WhatsApp, e formatá-lo como celular seria mentira. */}
										{contact.isGroup ? (
											<span className="text-xs">—</span>
										) : (
											<NumeroComBandeira numero={contact.number} />
										)}
									</TableCell>
									<TableCell className="hidden text-center text-muted-foreground sm:table-cell">
										{contact.email}
									</TableCell>

									{/* Etapa atual do Kanban (cartão mais recente) - coluna visível em
									    computador; grupo não tem cartão individual. */}
									{tipo !== "grupos" && !contact.isGroup && (
										<TableCell className="hidden text-center md:table-cell">
											<div className="flex items-center justify-center">
												<KanbanEtapaChip contactId={contact.id} showTitle size="sm" onCriar={() => setContatoParaKanban(contact)} />
											</div>
										</TableCell>
									)}
									{tipo !== "grupos" && contact.isGroup && (
										<TableCell className="hidden text-center md:table-cell">
											<span className="text-xs text-muted-foreground/70">—</span>
										</TableCell>
									)}

									{tipo === "grupos" && (
										<>
											<TableCell className="hidden text-center md:table-cell">
												{contact.fixedWhatsapp?.name ? (
													<span className="text-xs">
														{contact.fixedWhatsapp.name}
													</span>
												) : (
													<span className="text-xs text-muted-foreground">
														—
													</span>
												)}
											</TableCell>
											<TableCell className="hidden text-center md:table-cell">
												{contact.fixedQueue?.name ? (
													<Badge
														className="border-transparent text-[11px] text-white"
														style={{
															backgroundColor:
																contact.fixedQueue.color || "#64748b",
														}}
													>
														{contact.fixedQueue.name}
													</Badge>
												) : (
													<span className="text-xs text-muted-foreground">
														Sem fila
													</span>
												)}
											</TableCell>
										</>
									)}
									{etiquetas.length > 0 && (
										<TableCell className="hidden lg:table-cell">
											{contact.tags?.length ? (
												<div className="flex flex-wrap gap-1">
													{contact.tags.map((tag) => (
														<Badge
															key={tag.id}
															className="border-transparent text-[11px] text-white"
															style={{ backgroundColor: tag.color }}
														>
															{tag.name}
														</Badge>
													))}
												</div>
											) : (
												<span className="text-xs text-muted-foreground">—</span>
											)}
										</TableCell>
									)}
									<TableCell>
										<TooltipProvider>
											<div className="flex items-center justify-center gap-1">
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8"
															onClick={() => handleSaveTicket(contact)}
														>
															<MessageCircle className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>
														Abrir uma conversa com este contato
													</TooltipContent>
												</Tooltip>
												{/* Quatro botões na linha tomavam 172px dos 390 da
												    tela do celular. Editar e excluir descem para o
												    menu "..." ali do lado — continuam a um toque, sem
												    empurrar nome e telefone para fora. */}
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="hidden h-8 w-8 sm:inline-flex"
															onClick={() => hadleEditContact(contact.id)}
														>
															<Pencil className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>{i18n.t("contactModal.buttons.okEdit")}</TooltipContent>
												</Tooltip>
												{podeApagarContato && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="hidden h-8 w-8 text-destructive hover:text-destructive sm:inline-flex"
																onClick={(e) => {
																	setConfirmOpen(true);
																	setDeletingContact(contact);
																}}
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															{i18n.t("confirmationModal.buttons.confirm")}
														</TooltipContent>
													</Tooltip>
												)}
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button variant="ghost" size="icon" className="h-8 w-8">
															<MoreVertical className="h-4 w-4" />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end" className="w-72">
														{/* No celular estes dois não cabem na linha; aqui
														    são o mesmo comando, no mesmo lugar de sempre. */}
														<DropdownMenuItem
															className="sm:hidden"
															onClick={() => hadleEditContact(contact.id)}
														>
															<Pencil className="mr-2 h-4 w-4" />
															Editar contato
														</DropdownMenuItem>
														{podeApagarContato && (
															<DropdownMenuItem
																className="text-destructive focus:text-destructive sm:hidden"
																onClick={() => {
																	setConfirmOpen(true);
																	setDeletingContact(contact);
																}}
															>
																<Trash2 className="mr-2 h-4 w-4" />
																Excluir contato
															</DropdownMenuItem>
														)}
														{/* Grupo não vira cartão: o trabalho é de uma
														    pessoa, não da sala inteira. */}
														{!contact.isGroup && (
															<DropdownMenuItem
																className="flex-col items-start gap-0.5"
																onClick={() => setContatoParaKanban(contact)}
															>
																<span className="flex items-center">
																	<UserPlus className="mr-2 h-4 w-4" />
																	Adicionar ao Kanban
																</span>
																<span className="pl-6 text-xs text-muted-foreground">
																	Abre um cartão deste cliente na coluna que
																	você escolher.
																</span>
															</DropdownMenuItem>
														)}
														<DropdownMenuItem
															className="flex-col items-start gap-0.5"
															onClick={() => handleExportLGPD(contact)}
														>
															<span className="flex items-center">
																<FileDown className="mr-2 h-4 w-4" />
																Baixar tudo que guardamos dele
															</span>
															<span className="pl-6 text-xs text-muted-foreground">
																Gera um arquivo para entregar ao cliente que
																pedir seus dados (LGPD).
															</span>
														</DropdownMenuItem>
														<DropdownMenuItem
															className="flex-col items-start gap-0.5 text-destructive focus:text-destructive"
															onClick={() => setAnonymizingContact(contact)}
														>
															<span className="flex items-center">
																<UserX className="mr-2 h-4 w-4" />
																Apagar os dados pessoais dele
															</span>
															<span className="pl-6 text-xs text-muted-foreground">
																Nome, telefone e e-mail somem para sempre. As
																conversas ficam, sem dono (LGPD).
															</span>
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TooltipProvider>
									</TableCell>
								</TableRow>
							);
						})}
						{!loading && contacts.length === 0 && (
							<TableRow>
								<TableCell
									colSpan={(etiquetas.length > 0 ? 7 : 6) + (tipo !== "grupos" ? 1 : 0)}
									className="!p-0"
								>
									<EmptyStateCard
										variant="tableCell"
										size="md"
										intent="primary"
										icon={User}
										title={
											filtroDeTags.length
												? "Ninguém com essas etiquetas."
												: searchParam
													? "Ninguém com esse nome ou número."
													: "Nenhum contato ainda."
										}
										subtitle={
											(!filtroDeTags.length && !searchParam)
												? "Assim que alguém mandar mensagem, aparece aqui sozinho. Ou importe uma lista para começar."
												: undefined
										}
										action={
											(!filtroDeTags.length && !searchParam)
												? { label: "Novo contato", icon: Plus, onClick: handleOpenContactModal }
												: null
										}
									/>
								</TableCell>
							</TableRow>
						)}
						{loading && <TableRowSkeleton avatar columns={3} />}
					</TableBody>
				</Table>
			</div>

			{/* Lista do celular.
			    Mesmos dados da tabela, no formato que se lê no telefone: uma
			    linha por contato, tocável inteira, com o menu de ações à
			    direita. Sem colunas, sem rolagem lateral. */}
			<ul className="divide-y overflow-hidden rounded-xl border bg-card sm:hidden">
				{contacts.map((contact) => {
					const initials = (contact.name || "?")
						.split(" ")
						.slice(0, 2)
						.map((n) => n[0])
						.join("")
						.toUpperCase();

					return (
						<li key={contact.id} className="flex items-center gap-3 px-3 py-2.5">
							<Checkbox
								checked={selecionados.has(contact.id)}
								onCheckedChange={() => alternarSelecao(contact.id)}
								aria-label={`Selecionar ${contact.name || "contato sem nome"}`}
							/>

							{/* A linha toda abre a conversa: é o que se quer fazer
							    com um contato em 9 de 10 vezes. */}
							<button
								type="button"
								onClick={() => handleSaveTicket(contact)}
								className="flex min-w-0 flex-1 items-center gap-3 text-left"
							>
								<AvatarPreview
									className="h-10 w-10 shrink-0"
									previewUrl={contact.profilePicUrl}
									alt={contact.name}
								>
									<AvatarImage src={contact.profilePicUrl} alt={contact.name} />
									<AvatarFallback className="text-xs">
										{contact.isGroup ? <Users className="h-4 w-4" /> : initials}
									</AvatarFallback>
								</AvatarPreview>

								<div className="min-w-0 flex-1">
									<div className="flex items-center gap-1.5">
										<span className="truncate font-medium">
											{contact.name || (
												<span className="italic text-muted-foreground">
													Sem nome
												</span>
											)}
										</span>
										{contact.isGroup && (
											<span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
												Grupo
											</span>
										)}
									</div>
									<p className="truncate text-sm text-muted-foreground">
										{contact.isGroup ? (
											"Grupo"
										) : (
											<NumeroComBandeira numero={contact.number} />
										)}
									</p>
									{/* Kanban: etapa atual do cliente. No celular não tem tabela, então
									    o chip entra aqui, abaixo do número, alinhado com o nome.
									    Grupo não mostra. */}
									{tipo !== "grupos" && !contact.isGroup && (
										<div className="mt-1 flex flex-wrap items-center gap-1.5">
											<KanbanEtapaChip
												contactId={contact.id}
												size="xs"
												showTitle
												onCriar={() => setContatoParaKanban(contact)}
											/>
										</div>
									)}
									{contact.tags?.length > 0 && (
										<div className="mt-1 flex flex-wrap gap-1">
											{contact.tags.map((tag) => (
												<Badge
													key={tag.id}
													className="border-transparent px-1.5 py-0 text-[10px] text-white"
													style={{ backgroundColor: tag.color }}
												>
													{tag.name}
												</Badge>
											))}
										</div>
									)}
								</div>
							</button>

							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9 shrink-0"
										aria-label={`Ações de ${contact.name || "contato"}`}
									>
										<MoreVertical className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="w-64">
									<DropdownMenuItem onClick={() => handleSaveTicket(contact)}>
										<MessageCircle className="mr-2 h-4 w-4" />
										Conversar
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => hadleEditContact(contact.id)}>
										<Pencil className="mr-2 h-4 w-4" />
										Editar contato
									</DropdownMenuItem>
									{!contact.isGroup && (
										<DropdownMenuItem onClick={() => setContatoParaKanban(contact)}>
											<UserPlus className="mr-2 h-4 w-4" />
											Adicionar ao Kanban
										</DropdownMenuItem>
									)}
									<DropdownMenuItem onClick={() => handleExportLGPD(contact)}>
										<FileDown className="mr-2 h-4 w-4" />
										Baixar dados (LGPD)
									</DropdownMenuItem>
									<DropdownMenuItem
										className="text-destructive focus:text-destructive"
										onClick={() => setAnonymizingContact(contact)}
									>
										<UserX className="mr-2 h-4 w-4" />
										Apagar dados pessoais
									</DropdownMenuItem>
									<Can
										role={user.profile}
										perform="contacts-page:deleteContact"
										yes={() => (
											<DropdownMenuItem
												className="text-destructive focus:text-destructive"
												onClick={() => {
													setConfirmOpen(true);
													setDeletingContact(contact);
												}}
											>
												<Trash2 className="mr-2 h-4 w-4" />
												Excluir contato
											</DropdownMenuItem>
										)}
									/>
								</DropdownMenuContent>
							</DropdownMenu>
						</li>
					);
				})}

				{!loading && contacts.length === 0 && (
					<li className="px-4 py-10">
						<EmptyStateCard
							variant="tableCell"
							size="sm"
							intent="primary"
							icon={User}
							title={
								searchParam
									? "Ninguém com esse nome ou número."
									: "Nenhum contato ainda."
							}
							subtitle={
								!searchParam
									? "Assim que alguém mandar mensagem, aparece aqui sozinho."
									: undefined
							}
							action={
								!searchParam
									? { label: "Novo contato", icon: Plus, onClick: handleOpenContactModal }
									: null
							}
						/>
					</li>
				)}
				{loading && (
					<li className="px-4 py-6 text-center text-sm text-muted-foreground">
						Carregando...
					</li>
				)}
			</ul>
			</div>

			<AdicionarAoKanbanModal
				open={!!contatoParaKanban}
				contact={contatoParaKanban}
				onClose={() => setContatoParaKanban(null)}
			/>

			<NewTicketModal
				modalOpen={!!contatoParaConversa}
				contatoInicial={contatoParaConversa}
				onClose={() => setContatoParaConversa(null)}
			/>
		</MainContainer>
	);
};

export default Contacts;
